import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Grid,
    Typography,
    Paper,
    Card,
    CardContent,
    Chip,
    Avatar,
    LinearProgress,
    Stack,
    Divider,
} from '@mui/material';
import Logo from "../../assets/logo.jpg";
import { Star, Settings as SettingsIcon } from '@mui/icons-material';

// --- Constants ---
const MAX_POSSIBLE_SCORE = 60;
const USER_INITIAL_BG_COLOR = '#0a0068';
const PROGRESS_BAR_COLOR = '#0a0068';
const SUBTITLE_COLOR = '#cc4049';
const CARDS_PER_PAGE = 6; // 2 rows of 3 cards each

// --- Helper Functions ---
const calculatePercentageScore = (score, maxScore = MAX_POSSIBLE_SCORE) => {
    if (score === null || score === undefined || maxScore === 0) {
        return 0;
    }
    const percentage = (score / maxScore) * 100;
    return parseFloat(percentage.toFixed(0));
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
            const [day, month, year] = parts;
            const yearNum = parseInt(year, 10);
            if (yearNum > 1900 && yearNum < 2100) {
                const date = new Date(yearNum, parseInt(month, 10) - 1, parseInt(day, 10));
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            }
        }
    }
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (e) {
        return dateString;
    }
};

const getInitials = (fname) => {
    return fname ? fname[0].toUpperCase() : '';
};

// --- Reusable Header Component ---
const ReportHeader = ({ assessment, client, activeTab }) => (
    <Card
        elevation={10}
        sx={{
            mb: 3,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #0a0068 0%, #4a004b 60%, #cc4049 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '@media print': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid'
            }
        }}
    >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container alignItems="center" spacing={{ xs: 1, md: 2 }}>
                <Grid item>
                    <Box sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        p: 0.25
                    }}>
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                maxWidth: '90%',
                                maxHeight: '90%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs>
                    <Box sx={{ textAlign: 'center', px: { xs: 0, md: 1 } }}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                mb: 0.5,
                                textShadow: '0 2px 5px rgba(0,0,0,0.25)',
                                fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }
                            }}
                        >
                            {assessment.assignment_name || 'Assessment Report'}
                        </Typography>
                        <Chip
                            icon={<Star sx={{ color: 'white !important', fontSize: '1rem !important' }} />}
                            label="Assessment Report"
                            variant="outlined"
                            size="small"
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.4)',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                py: 0.2,
                                height: 'auto',
                                '& .MuiChip-icon': { color: 'white', ml: '4px' },
                                '& .MuiChip-label': { py: '2px' }
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item sx={{ minWidth: { xs: 'auto', md: 150 } }}>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, pl: { xs: 1, sm: 0 } }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.8rem',
                            }}>
                            Client: {client}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                            Date: {formatDate(assessment.created_at)}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </CardContent>
    </Card>
);

// --- Individual User Performance Card Component ---
const UserPerformanceCard = ({ submission, maxScore }) => {
    const userDetails = submission.results.user_details;
    const finalScore = submission.results.finalScore;
    const percentage = calculatePercentageScore(finalScore, maxScore);
    const competency = submission.results.finalInterpretation?.name || 'N/A';
    const experienceLevel = userDetails.experience_level || 'N/A';
    const competencyChipIcon = competency.toLowerCase() === 'developing' ?
        <SettingsIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /> : undefined;

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Card sx={{
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                height: '100%',
                '@media print': {
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid'
                }
            }}>
                <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: USER_INITIAL_BG_COLOR, width: 36, height: 36, fontSize: '1rem' }}>
                            {getInitials(userDetails.fname)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: '600', lineHeight: 1.3 }}>
                                {userDetails.fname} {userDetails.lname}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {submission.results.user_id}
                            </Typography>
                        </Box>
                    </Stack>

                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.75rem' }}>
                                {finalScore}/{maxScore}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                {percentage}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                                height: 8,
                                borderRadius: 5,
                                [`& .MuiLinearProgress-bar`]: {
                                    backgroundColor: PROGRESS_BAR_COLOR,
                                },
                                backgroundColor: (theme) => theme.palette.grey[200]
                            }}
                        />
                    </Box>

                    <Chip
                        icon={competencyChipIcon}
                        label={competency}
                        size="small"
                        sx={{
                            backgroundColor: competency.toLowerCase() === 'proficient' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0,0,0,0.05)',
                            color: competency.toLowerCase() === 'proficient' ? '#2e7d32' : 'text.secondary',
                            fontWeight: 500,
                            width: 'fit-content',
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            fontSize: '0.75rem',
                            '.MuiChip-icon': { ml: '5px', fontSize: '0.9rem' }
                        }}
                    />

                    <Divider sx={{ my: 1 }} />

                    <Box>
                        {/* <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Client: {userDetails.user_batch}
                        </Typography> */}
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Experience Level: {experienceLevel}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
};

// --- Page Component ---
const ReportPage = ({ assessment, client, activeTab, submissions, pageNumber, isFirstPage, isLastPage }) => (
    <Box
        sx={{
            p: { xs: 2, sm: 3, md: 4 },
            position: 'relative',
            '@media print': {
                pageBreakAfter: isLastPage ? 'auto' : 'always',
                breakInside: 'avoid'
            }
        }}
    >
        {/* Header on every page */}
        <ReportHeader assessment={assessment} client={client} activeTab={activeTab} />

        {/* Section titles only on first page */}
        {isFirstPage && (
            <Box sx={{ mt: 3, mb: 2.5 }}>
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        color: USER_INITIAL_BG_COLOR,
                        mb: 0.25,
                        fontSize: { xs: '1.3rem', md: '1.6rem' }
                    }}
                >
                    Detailed User Performance
                </Typography>
            </Box>
        )}

        {/* User Cards Grid */}
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
            {submissions.map((submission) => (
                <UserPerformanceCard
                    key={submission.results?.user_id}
                    submission={submission}
                    maxScore={MAX_POSSIBLE_SCORE}
                />
            ))}
        </Grid>

        {/* Page number */}
        <Box sx={{
            textAlign: 'center',
            mt: 4,
            pt: 2,
            borderTop: '1px solid #e0e0e0',
            '@media print': {
                // Remove fixed positioning
                textAlign: 'center',
                // border: 'none',
                mt: 2,
                pt: 1
            }
        }}>
            <Typography variant="caption" color="text.secondary">
                Page {pageNumber}
            </Typography>
        </Box>
    </Box>
);

// --- Main Preview Client Result Component ---
const PreviewClientResult = ({ assessment, client, submissions, activeTab }) => {
    const filteredSubmissions = useMemo(() => {
        if (!submissions || !activeTab) return [];
        return submissions.filter(
            (submission) => submission.results?.user_details?.user_batch === activeTab
        );
    }, [submissions, activeTab]);

    // Split submissions into pages
    const pages = useMemo(() => {
        const pagesArray = [];
        for (let i = 0; i < filteredSubmissions.length; i += CARDS_PER_PAGE) {
            pagesArray.push(filteredSubmissions.slice(i, i + CARDS_PER_PAGE));
        }
        return pagesArray;
    }, [filteredSubmissions]);

    if (!assessment || !client) {
        return (
            <Paper sx={{ p: 3, m: 3, textAlign: 'center' }}>
                <Typography variant="h6">Loading assessment data...</Typography>
            </Paper>
        );
    }

    if (filteredSubmissions.length === 0) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <ReportHeader assessment={assessment} client={client} activeTab={activeTab} />
                <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        No submissions found for {activeTab || 'the selected criteria'}.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{
            '@media print': {
                margin: 0,
                padding: 0,
                fontSize: '12pt',
                lineHeight: 1.4
            }
        }}>
            {pages.map((pageSubmissions, pageIndex) => {
                const currentPageNumber = pageIndex + 1; // Capture the value explicitly
                return (
                    <ReportPage
                        key={pageIndex}
                        assessment={assessment}
                        client={client}
                        activeTab={activeTab}
                        submissions={pageSubmissions}
                        pageNumber={currentPageNumber}
                        isFirstPage={pageIndex === 0}
                        isLastPage={pageIndex === pages.length - 1}
                    />
                );
            })}
        </Box>
    );
};

export default PreviewClientResult;